import { api } from './client';

/**
 * Админка: тарифы, подписки, индивидуальные условия, сотрудники.
 *
 * ПУТИ ИДУТ ОТ `/api/rk`, как и всё остальное: у сервиса один общий
 * префикс, и админка не исключение. Отдельный корень означал бы вторую
 * настройку раздачи, второй разбор ключа доступа и второй повод им
 * разойтись.
 */

export interface Overage {
  metric: string;
  included: number;
  rate: number;
}

export interface AdminMe {
  role: 'author' | 'editor' | 'support' | 'billing' | 'owner';
  title: string;
  rights: string[];
  /**
   * Почта вошедшего — для метки в шапке.
   *
   * ИМЕНИ У СОТРУДНИКА В СИСТЕМЕ НЕТ (вопрос 71): доступ выдаётся
   * по почте, её и показываем на месте имени эталона. `null` — запись
   * сотрудника уже удалили, пока он сидел на странице; тогда в метке
   * остаётся одна роль, и она не врёт.
   */
  email: string | null;
}

export interface PlanPrice {
  id: string;
  baseKopecks: number;
  baseText: string;
  overage: Overage[];
  /** Скидка на первую оплату, проценты. `null` — скидки нет. */
  introDiscountPercent: number | null;
  effectiveFrom: string;
  note: string;
  createdBy?: string;
  createdAt?: string;
}

/** Срок тарифа со своей ценой: месяц, три месяца, год. */
export interface PlanTerm {
  id: string;
  name: string;
  /** `null` — бессрочный. */
  durationDays: number | null;
  sortOrder: number;
  archived: boolean;
  /** `null` — цены нет, и продать срок нельзя. */
  price: PlanPrice | null;
  priceVersions: number;
}

/**
 * Ступень лестницы тарифа (передача 07.09.2026).
 *
 * `add` — доплата к базе В МЕСЯЦ, в копейках. У базы всегда 0.
 * `locked` ставит СЕРВЕР: доплату заведённого тарифа правит только
 * новая версия цены (ТЗ 1.3), и решение об этом принимается там,
 * а не здесь.
 */
export interface AdminStep {
  n: number;
  free?: boolean;
  add: number;
  locked?: boolean;
}

export interface AdminPlan {
  id: string;
  code: string;
  name: string;
  tagline: string | null;
  limits: Record<string, number>;
  /**
   * Потолки СЛОВАМИ ЭТАЛОНА: «1 кабинет, до 50 кампаний».
   *
   * ПРИХОДЯТ ГОТОВЫМИ С СЕРВЕРА, а не собираются здесь. `limits` —
   * это ключи базы, и показывать их человеку нельзя; собери слова
   * второй раз в кабинете — и они разойдутся с карточкой на сайте
   * в день, когда заведут третью величину.
   */
  limitsWords: string;
  /**
   * Две лестницы ступеней. Первая ступень — база с доплатой 0:
   * её цена стоит в таблице сроков.
   */
  cabSteps: AdminStep[];
  campSteps: AdminStep[];
  /** Сколько сочетаний цены продаёт тариф: 1 — выбора у клиента нет. */
  stepCombos: number;
  /** Разделы, которые тариф открывает. Пусто — открыто всё. */
  features: string[];
  public: boolean;
  sortOrder: number;
  /**
   * Кому тариф показывается: `seller`, `agency` или `both`.
   *
   * `both` — И УМОЛЧАНИЕ, И РАБОЧЕЕ СОСТОЯНИЕ: «Бесплатный»
   * и «Пробный» одинаково нужны обоим.
   */
  audience: string;
  /** Плашка на карточке сайта: «берут чаще». Пусто — плашки нет. */
  badge: string | null;
  /** Кому выставлен индивидуально. `null` — общий тариф. */
  forWorkspaceId: string | null;
  /**
   * Раздел: site | draft | personal | contract | archive.
   * Считает сервер — правило одно на службу и на экран.
   */
  shelf: string;
  archived: boolean;
  /**
   * Этот тариф выдаётся новичку как пробный период.
   * Отмечен ровно один; ни одного — пробного периода нет.
   */
  isTrial: boolean;
  /**
   * Сюда падает пространство, когда подписка кончилась (решение Р-3).
   * Отмечен ровно один; ни одного — окончание ничего не меняет.
   */
  isFree: boolean;
  terms: PlanTerm[];
}

/**
 * Рубильник тарифной сетки на сайте.
 *
 * ОТДЕЛЬНО ОТ ГАЛОЧКИ «ПУБЛИЧНЫЙ» У ТАРИФА: галочка говорит «этот
 * тариф скрыт нарочно», рубильник — «витрина закрыта целиком».
 */
export interface GridState {
  published: boolean;
  /** Состояние словами — его и показываем, а не «вкл/выкл». */
  text: string;
  reason: string | null;
  by: string | null;
  at: string | null;
}

/** Полка тарифной сетки: кому тариф показывается. */
export interface PlanAudience {
  code: string;
  title: string;
  hint: string;
}

export interface ServiceModule {
  code: string;
  title: string;
  hint: string;
  /**
   * Пометка рядом с названием: «скоро» — или `null`, если помечать
   * нечем. ПРИХОДИТ С СЕРВЕРА, А НЕ ВЫЧИСЛЯЕТСЯ ЗДЕСЬ: список
   * недописанных разделов живёт в одном месте, `src/billing/modules.ts`,
   * и вторая его копия в кабинете разошлась бы с первой в день, когда
   * раздел допишут.
   */
  note: string | null;
}

export interface BulkRow {
  id: string;
  scope: string;
  scopeTitle: string;
  workspaceIds: string[];
  days: number;
  reason: string;
  issuedBy: string;
  createdAt: string;
  appliedAt: string | null;
  affected: number | null;
  skipped: number | null;
  /** Сутки легли подарком — сверх срока, не сгорая при продлении. */
  gift: boolean;
}

export interface AdminGrant {
  id: string;
  workspaceId: string;
  /** Имя пространства. `null` — не нашлось; тогда показываем ууид. */
  workspaceName: string | null;
  days: number | null;
  priceKopecks: number | null;
  priceText: string | null;
  limits: Record<string, number> | null;
  reason: string;
  issuedBy: string;
  issuedAt: string;
  expiresAt: string | null;
  /**
   * Отозвано. В списке «Кто на нестандартных условиях» этого поля нет —
   * там только действующие, — а в карточке пространства показывается
   * вся история: «когда перестали давать скидку» спрашивают.
   */
  revokedAt?: string | null;
}

export interface StaffMember {
  userId: string;
  /**
   * ═════════════════════════════════════════════════════════════════════
   * КАК ЕГО ЗОВУТ. ПУСТАЯ СТРОКА — «НЕ НАЗВАЛИ».
   *
   * Эталон рисует имя над почтой и инициалы в кружке слева. Взять их
   * из учётной записи нельзя: у записи клиента имени нет вовсе —
   * регистрация просит почту и пароль. Сотрудника заводит владелец
   * руками и знает, как его зовут, поэтому имя и хранится у выдачи.
   *
   * Не `null`, а пустая строка: два разных способа сказать «имени нет»
   * — это два способа его проверить, и один из них однажды забудут.
   * ═════════════════════════════════════════════════════════════════════
   */
  name: string;
  /** Почта — то, чем сотрудника называют вслух. `null` — записи уже нет. */
  email: string | null;
  /**
   * Включена ли двухфакторка. Метка «без двухфакторки» в списке —
   * единственное место, где видно дыру в безопасности: сотрудник
   * с правами на деньги и без второго ключа стоит всей кассы.
   */
  hasTotp: boolean;
  role: string;
  /** Заведённая роль. `null` — доступ выдан до переноса, по слову. */
  roleId: string | null;
  title: string;
  /** Расстановка ступеней его роли. */
  grid: RoleGrid;
  /**
   * ═════════════════════════════════════════════════════════════════════
   * ЧТО ДАЁТ ЕГО РОЛЬ — ПОТОЛОК, ИЗ КОТОРОГО ВЫЧИТАЮТ СНЯТОЕ.
   *
   * С СЕРВЕРА И ТЕМ ЖЕ СЧЁТОМ, ЧТО И ДОСТУП. Экран считал потолок сам,
   * по слову роли: `roles.find((r) => r.code === member.role)?.rights`.
   * Это второй источник правды, и он разошёлся с первым — слово
   * говорило «владелец» и давало все 28 прав, расстановка заведённой
   * роли давала 11 строк из 17. Экран рисовал галочки там, куда сервер
   * не пускает, и по нему выходило, что с правами всё в порядке.
   *
   * СНЯТОЕ ПОИМЁННО СЮДА НЕ ВХОДИТ — оно в `denied` и вычитается
   * галочкой, а не отсутствием строки.
   * ═════════════════════════════════════════════════════════════════════
   */
  roleRights: string[];
  /**
   * Что снято сверх роли. Пусто — набор роли целиком.
   *
   * ТОЛЬКО ВЫЧИТАНИЕ: роль остаётся потолком, добавить право мимо
   * неё нечем и здесь.
   */
  denied: string[];
  rightsChangedBy: string | null;
  rightsChangedAt: string | null;
  grantedBy: string;
  grantReason: string;
  grantedAt: string;
}

/** Право с человеческим названием — для экрана настройки. */
export interface RightInfo {
  code: string;
  title: string;
  hint: string;
}

export interface RightSection {
  title: string;
  rights: RightInfo[];
}

export interface StaffRole {
  code: string;
  title: string;
  /** Что даёт роль. Из этого набора и вычитают. */
  rights: string[];
}

/** Расстановка: право → ступень. Чего нет — «нет». */
export type RoleGrid = Record<string, string>;

/** Одна ступень лестницы: слово и что оно даёт. */
export interface StepInfo {
  step: string;
  note: string;
}

/** Право на экране расстановки. */
export interface Pravo {
  id: string;
  title: string;
  hint: string;
  /** Красная приписка, если ступень выше «нет» опасна. */
  warn: string | null;
  steps: StepInfo[];
}

/** Группа прав — одна из пяти. */
export interface RightGroup {
  title: string;
  prava: Pravo[];
}

/** Заведённая роль — карточка для списка. */
export interface BuiltRole {
  id: string;
  name: string;
  note: string;
  grid: RoleGrid;
  /** Сколько прав за ролью. */
  weight: number;
  /** Сколько человек на ней сидит. */
  people: number;
  summary: string;
  warnings: string[];
  empty: boolean;
  /** Слово встроенной роли, из которой перенесена. */
  fromCode: string | null;
}

/** Одна величина, по которой новый тариф теснее прежнего. */
export interface Tightening {
  key: string;
  /** По-русски: «кабинетов», «кампаний». */
  what: string;
  used: number;
  limit: number;
}

/** Что будет, если сменить тариф. */
export type PlanChangePreview =
  | { ok: false; code: string; reason: string }
  | { ok: true; tightenings: Tightening[]; warning: string | null };

/** Одна смена тарифа в прошлом. */
export interface PlanChangeRow {
  id: string;
  fromTitle: string;
  toTitle: string;
  reason: string;
  actor: string;
  /** Что было тесно — как это показали нажимавшему. */
  warning: string | null;
  createdAt: string;
}

export interface AdminSpace {
  id: string;
  name: string;
  blocked: boolean;
  /** Почему заблокировано — словами того, кто блокировал. */
  blockedReason: string | null;
  createdAt: string;
  ownerUserId: string;
  ownerEmail: string | null;
  /** `seller` | `agency` | `unknown`. «Не спрашивали» — законное значение. */
  clientType: string;
  members: number;
  cabinets: number;
  /** Сколько кампаний в действующих кабинетах — графа эталона. */
  campaigns: number;
  /**
   * Сколько кампаний ЖИВЫХ: заведены и не закончились.
   *
   * ═════════════════════════════════════════════════════════════════
   * ЖИВАЯ — НЕ «АКТИВНАЯ». Первая редакция считала только статус
   * «активна» и показала 2 там, где владелец ждал 36. Замер
   * по живой базе 26.08.2026 объяснил разницу:
   *
   *     готова      4 →   2
   *     завершена   7 → 428
   *     активна     9 →   2
   *     пауза      11 →  32
   *
   * Всего 464, живых 36. Четыре сотни завершённых — это история
   * клиента, а не он сам: по общему числу нельзя понять, велик ли
   * он сейчас. А «активна» — это состояние ЭТОГО ЧАСА: кампания
   * на паузе по расписанию вернётся вечером и никуда не делась.
   * ═════════════════════════════════════════════════════════════════
   */
  live: number;
  /**
   * Сколько кампаний ведёт наш биддер: переключатель в кабинете.
   *
   * СЧИТАЕТСЯ ОТДЕЛЬНО ОТ `live`, А НЕ ВНУТРИ ЧИСЛА АКТИВНЫХ.
   * Первая редакция брала эти числа вложенно — «включён биддер
   * среди активных» — и показала ноль там, где биддер вёл две
   * кампании: обе в тот час стояли на паузе.
   */
  managed: number;
  cabinetNames: string[];
  planCode: string | null;
  planName: string | null;
  expiresAt: string | null;
  /** `null` — бессрочная подписка: числа здесь нет, а не «много». */
  daysLeft: number | null;
  health: 'active' | 'ending' | 'expired';
  frozen: boolean;
  /**
   * Когда заморозка кончится сама. `null` — бессрочная либо не заморожено.
   *
   * «Заморожено» без «до когда» сотрудник читает как «навсегда»
   * и идёт снимать руками то, что снимется само.
   */
  frozenUntil: string | null;
  nonStandard: boolean;
  /**
   * Сколько условий действует. Окно «Обнулить» показывает число, как
   * у эталона: снять одно и снять четыре — разные решения.
   */
  grantsLive: number;

  /**
   * Состояние одним значением: `blocked | bypass | frozen | working`.
   *
   * Считает сервер (`spaceState()`), потому что важен ПОРЯДОК: беда
   * перекрывает обход, обход перекрывает заморозку. Повтори мы этот
   * порядок здесь — однажды повторили бы иначе.
   */
  state: 'blocked' | 'bypass' | 'frozen' | 'working';

  /**
   * Разбор обхода пробного (п. 1 передачи). `null` — совпадения нет.
   *
   * «Обход» без чисел — это обвинение без доказательства. Здесь: какой
   * кабинет совпал, сколько суток израсходовала прежняя запись,
   * сколько выдано этой и когда совпадение поймано.
   */
  trialBypass: {
    matchedCabinetId: string;
    prevSpaceId: string;
    prevDaysUsed: number;
    grantedDays: number;
    detectedAt: string;
  } | null;
}

export interface LedgerEntry {
  deltaDays: number;
  resetsTerm: boolean;
  expiresBefore: string | null;
  expiresAfter: string;
  source: string;
  reason: string;
  actor: string;
  createdAt: string;
  /** По какой оплате продлили. `null` — продление не за деньги. */
  paymentId: string | null;
}

/** Оплата, отмеченная руками (FR-6.4). */
export interface AdminPayment {
  id: string;
  workspaceId: string;
  /** Название пространства. `null` — пространство удалили. */
  workspaceName: string | null;
  planId: string;
  termId: string;
  amountKopecks: number;
  /** Сумма строкой — считать её на экране заново незачем. */
  amountText: string;
  method: string;
  methodTitle: string;
  reference: string;
  confirmedBy: string;
  confirmedAt: string;
}

export interface PaymentMethodOption {
  code: string;
  title: string;
}

export interface SpaceSubscription {
  plan: { id: string; code: string; name: string };
  price: { baseKopecks: number; baseText: string; overage: Overage[] };
  limits: Record<string, number>;
  expiresAt: string | null;
  daysLeft: number | null;
  health: string;
  frozen: boolean;
  nonStandard: boolean;
  /** Журнал разошёлся с полем — кто-то правил дату мимо журнала. */
  drifted: boolean;
  problems: Array<{ index: number; reason: string }>;
  grants: AdminGrant[];
}


/** Наблюдение за сервисом (FR-23). */
export interface JobState {
  /** Recorded queue transitions; not evidence that a data refresh completed. */
  activity?: {
    state: 'running' | 'deferred' | 'completed' | 'failed' | 'skipped' | 'unknown';
    observedAt: number | null;
    lastAdvanceAt: number | null;
    activeCount: number | null;
    waitingCount: number | null;
    nextRetry: { retryAt: number | null; reason: 'deferred' | 'busy' | null } | null;
  };
  name: string;
  title: string;
  everyMin: number;
  lastAt: string | null;
  ageText: string;
  verdict: 'ok' | 'late' | 'stuck' | 'never';
  ok: number;
  failed: number;
  /** Пропущено в последнем проходе: кабинеты были заняты. Заход 542. */
  skipped: number;
  error: string | null;
}

export interface RecoveryState {
  level: 'ok' | 'warn' | 'bad';
  observedAt: { primary: string | null; secondary: string | null };
  items: Array<{
    id: 'database' | 'service-state' | 'application-restore' | 'configuration-restore';
    title: string; level: 'ok' | 'warn' | 'bad'; status: 'ok' | 'late' | 'failed' | 'pending' | 'unavailable';
    createdAt: string | null; verifiedAt: string | null; bytes: number | null; files: number | null; text: string;
  }>;
}

export interface HealthSnapshot {
  /** External proof times; absent on an API from the previous release. */
  recovery?: RecoveryState | null;
  level: 'ok' | 'warn' | 'bad';
  summary: string;
  jobs: JobState[];
  breakers: Array<{ cabinetId: string; domain: string; note: string }>;
  badTokens: Array<{
    cabinetId: string;
    name: string;
    tail: string;
    checkedAt: string | null;
    error: string;
  }>;
  failureShare: number | null;
  attempts: number;
  rejections: Array<{ limitKey: string; perSeller: boolean; total: number }>;
  slots: { global: number; limit: number };
  /**
   * Очереди задач (ТЗ, Р14). `null` — наблюдение за ними не подключено.
   *
   * ВЕРДИКТ ПРИХОДИТ ГОТОВЫМ, а не считается здесь: правило одно,
   * и жить оно должно на сервере — иначе письмо о тревоге и экран
   * однажды скажут разное про одно и то же число.
   *
   * `воркеров: null` — «не сосчитал», и это НЕ ноль. Ноль воркеров
   * при непустой очереди будит ночью; «не сосчитал» — повод чинить
   * наблюдение.
   */
  ochered: {
    очереди: Array<{
      имя: string;
      ждут: number;
      идут: number;
      отложены: number;
      отказали: number;
    }>;
    воркеров: number | null;
    подняты: boolean;
    вердикт: { уровень: 'ok' | 'warn' | 'down'; текст: string };
  } | null;
  /** Резервные копии (НФ-13). `null` — не смогли прочитать. */
  backup: {
    verdict: 'ok' | 'late' | 'stuck' | 'never' | 'shrunk';
    lastAt: string | null;
    bytes: number | null;
    previousBytes: number | null;
    count: number | null;
    text: string;
  } | null;
  /** Проверка восстановления — вторая половина НФ-13 и та, что пропускают. */
  restore: {
    verdict: 'ok' | 'late' | 'never';
    lastAt: string | null;
    file: string | null;
    text: string;
  } | null;
  /**
   * Очередь: глубина и возраст самой старой выгрузки.
   *
   * ЧИСЛО САМО ПО СЕБЕ НИЧЕГО НЕ ЗНАЧИТ: двадцать выгрузок в ночную
   * догрузку — норма, двадцать в полдень — беда. Отличает их возраст.
   */
  queue: {
    depth: number;
    oldestMin: number | null;
    oldestKind: string | null;
    byKind: Array<{ kind: string; count: number }>;
    level: 'ok' | 'warn' | 'bad';
    text: string;
  } | null;
  /**
   * Почта. Без неё не работают ни регистрация, ни восстановление пароля,
   * и заметить это некому: человек, не получивший письмо, не жалуется.
   */
  mail: {
    verdict: 'ok' | 'not-configured' | 'unreachable' | 'unchecked';
    text: string;
    server: string;
  } | null;
  /**
   * ═══════════════════════════════════════════════════════════════════
   * УЗЛЫ (заход 549): кто жив, какая роль, какая машина, какая сборка.
   *
   * Пустой список — это «реестр не прочитан», а не «узлов нет»: сам
   * себя узел записывает всегда, и пустота значит, что не ответил
   * Redis. Плитка говорит именно это, а не «ноль узлов».
   * ═══════════════════════════════════════════════════════════════════
   */
  узлы: Array<{
    имя: string;
    роль: string;
    машина: string;
    сборка: string;
    собрано: string;
    поднят: number;
    отмечен: number;
    разом: number | null;
  }>;
  /** Что не удалось собрать — честно, а не молча. */
  blind: string[];
}

export interface AdminJournalRow {
  id: string;
  at: string;
  kind: string;
  action: string;
  reason: string | null;
  result: string;
  tone: 'good' | 'warn' | 'bad' | 'plain';
  by: 'служба' | 'человек' | 'правило';
  error: string | null;
  target: string | null;
  cabinetId: string | null;
  cabinetName: string | null;
}

export interface WritesState {
  on: boolean;
  reason: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
  fromEnv: boolean;
  mayStop: boolean;
  mayStart: boolean;
  /** Может ли этот сотрудник стереть все ключи Wildberries. */
  mayEraseKeys: boolean;
}

/** Сколько сотрётся, если нажать. Спрашивается ДО нажатия. */
export interface EraseKeysPreview {
  ключей: number;
  кабинетов: number;
  названий: number;
}


/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПРИЗНАК ВЫПУСКА В ВИДЕ, ГОТОВОМ К ПОКАЗУ (ТЗ 19, часть II, раздел 10).
 *
 * `words`, `days`, `overdue` СЧИТАЕТ СЕРВЕР, а не экран. Правило
 * «тридцать суток от заведения» и склонение «включён 2 пространствам»
 * живут в `src/staff/priznaki.ts` — там же, где решают, можно ли
 * признак снять. Посчитай их ещё раз здесь — и через месяц экран
 * начнёт называть тридцать суток тридцатью одними, а отказ на снятие
 * придёт из другого места и с другими словами.
 *
 * `mismatch` — то, ради чего экран вообще нужен: ворота отвечают
 * не то, что лежит в таблице. Разбор полёта PostHog (февраль 2026):
 * правка признака отображалась в их же экране, а до кода не доходила
 * четверо суток.
 * ═══════════════════════════════════════════════════════════════════════
 */
export interface ReleaseFlag {
  key: string;
  state: 'off' | 'workspaces' | 'on';
  workspaces: string[];
  /** Имена пространств в том же порядке: опознаватель человеку не читается. */
  workspaceNames: string[];
  note: string | null;
  /** Состояние словами — те же, что уходят в журнал. */
  words: string;
  /** Сколько суток признак живёт. Счёт ОТ ЗАВЕДЕНИЯ, его не сдвинуть. */
  days: number;
  /** Висит в `workspaces` дольше срока — пора снимать. */
  overdue: boolean;
  createdAt: string;
  retiredAt: string | null;
  updatedBy: string | null;
  updatedAt: string;
  /** Короткая память ворот спорит с таблицей. */
  mismatch: boolean;
}

export interface ReleaseFlags {
  flags: ReleaseFlag[];
  /** Через сколько суток зажигается полоса «пора снять». */
  overdueAfterDays: number;
  mayWrite: boolean;
  mayRelease: boolean;
}

// ── Персональные данные (ТЗ часть I раздел 3) ──────────────────────

/** Строка списка обращений. `осталось` — рабочих дней до срока. */
export interface PdRequestRow {
  id: string;
  получено: string;
  почта: string;
  пространство: string | null;
  пространствоId: string | null;
  вид: string;
  видКлюч: string;
  состояние: 'новое' | 'в работе' | 'продлено' | 'отвечено';
  срок: string;
  осталось: number;
  просрочено: boolean;
  можноПродлить: boolean;
}

export interface PdRequestCard extends PdRequestRow {
  текст: string;
  ответ: string | null;
  вложения: string[];
  справка: {
    workspaceName: string | null;
    registeredAt: string | null;
    cabinets: number;
    subscription: string | null;
  } | null;
  категории: Array<{ имя: string; есть: boolean }>;
  журнал: Array<{ когда: string; кто: string; что: string; подробность: string | null }>;
  mayWrite: boolean;
}

export interface PdRequests {
  строки: PdRequestRow[];
  счёт: { всего: number; требуют: number; просрочено: number; отвечено: number };
  отборы: Array<{ ключ: 'need' | 'all' | 'month' | 'kind'; сколько: number }>;
  виды: Record<string, string>;
  mayWrite: boolean;
}

export interface PdRkn {
  заведено: boolean;
  номер: string | null;
  ключ: string | null;
  поданное: { обработчики: string[]; категории: string[] } | null;
  сейчас: { обработчики: string[]; категории: string[] };
  расхождения: Array<{ графа: string; было: string; стало: string }>;
  предупреждение: { заголовок: string; текст: string } | null;
  податьДо: string | null;
  подачи: Array<{
    когда: string;
    кто: string;
    обработчики: string;
    категории: string;
    примечание: string | null;
  }>;
  mayWrite: boolean;
}

export interface PdEditionRow {
  id: string;
  документ: string;
  номер: number;
  имя: string;
  вСилеС: string;
  состояние: string;
  опубликована: string;
  ктоОпубликовал: string;
  приняли: number;
  изменения: string[];
}

export interface PdEditions {
  строки: PdEditionRow[];
  действуют: Array<{ документ: string; номер: number | null }>;
  следующие: Array<{ документ: string; номер: number }>;
  documents: string[];
  mayWrite: boolean;
}

export interface PdProcessorRow {
  id: string;
  наименование: string;
  инн: string;
  чтоДелает: string;
  где: string;
  договор: string;
  тревога: boolean;
  заметка: string;
  действующий: boolean;
}

export interface PdProcessors {
  строки: PdProcessorRow[];
  безДоговора: number;
  заметкаТревоги: string;
  mayWrite: boolean;
}

export interface PdAdmissionRow {
  id: string;
  фио: string;
  должность: string;
  с: string;
  обязательство: string;
  категории: string;
  состояние: 'действует' | 'отозван';
  тревога: boolean;
  заметка: string;
  политика: string | null;
}

export interface PdAdmissions {
  строки: PdAdmissionRow[];
  тревожных: number;
  поводОзнакомить: string | null;
  категорииПолитики: string[];
  mayWrite: boolean;
}

/** Числа на пяти закладках. `null` у «Редакций» — числа не бывает. */
export interface PdTabs {
  counts: Record<string, number | null>;
  mayWrite: boolean;
}

// ── Наблюдение сверх здоровья службы (ТЗ часть II раздел 3) ────────

export interface NabTabs {
  /** `null` — числа не бывает, а не «сейчас ноль». */
  counts: Record<string, number | null>;
  tabs: Array<{ key: string; label: string }>;
  mayWrite: boolean;
  mayErase: boolean;
}

export interface NabKeep {
  строки: Array<{
    key: string;
    что: string;
    хранить: string;
    убрано: string;
    очередь: string;
    отстаёт: boolean;
    заметка: string;
    когда: string | null;
  }>;
  отстаёт: boolean;
  история: Array<{
    когда: string;
    убрано: number;
    очередь: number;
    мс: number;
    сОчередью: boolean;
  }>;
  итог: { убрано: string; очередь: string };
  mayWrite: boolean;
}

export interface NabDeletions {
  строки: Array<{
    id: string;
    поступил: string;
    почта: string;
    пространство: string;
    пространствоId: string | null;
    осталось: number;
    доОтмены: number;
    заперто: boolean;
    можно: boolean;
    просрочено: boolean;
    почемуНельзя: string | null;
    подписьОтмены: string;
  }>;
  счёт: { ожидают: number; можноОбезличить: number; просрочено: number };
  строкаСчёта: string;
  цвет: 'bad' | 'warn' | 'good';
  слово: string;
  последствия: Array<{ что: string; станет: string; тревожно: boolean }>;
  mayWrite: boolean;
  mayErase: boolean;
}

export interface NabLetters {
  строки: Array<{
    id: string;
    когда: string;
    направление: string;
    почта: string;
    сторона: string;
    овём: string;
    состояние: string;
    доставка: string | null;
    срок: string | null;
    осталось: number | null;
    тревожно: boolean;
    можноОтветить: boolean;
    клиентОтветил: string | null;
  }>;
  отборы: Array<{ ключ: string; имя: string; сколько: number }>;
  счёт: Record<string, number>;
  подписьПолосы: string;
  mayWrite: boolean;
}

export interface NabLetter {
  id: string;
  когда: string;
  почта: string;
  сторона: string;
  овём: string;
  текст: string;
  вложения: string[];
  вЖурнале: string[];
  вКабинете: string[];
  mayWrite: boolean;
}

export interface NabClaims {
  строки: Array<{
    id: string;
    получена: string;
    кто: string;
    очём: string;
    откуда: string;
    номерОбращения: number | null;
    разбирает: string;
    состояние: string;
    срок: string;
    подписьСрока: string;
    просрочена: boolean;
    можноОтветить: boolean;
    решение: string | null;
  }>;
  счёт: { вРаботе: number; просрочено: number };
  строкаСчёта: string;
  цвет: 'bad' | 'warn' | 'good';
  решения: string[];
  днейНаОтвет: number;
  mayWrite: boolean;
}

// ── Подразделы «Оплат» (ТЗ часть II раздел 2) ──────────────────────

export interface PayTabs {
  /** `null` — числа не бывает: отчёт, а не очередь дел. */
  counts: Record<string, number | null>;
  mayRefund: boolean;
  mayContract: boolean;
}

export interface PayRefunds {
  строки: Array<{
    id: string;
    пространство: string;
    пространствоId: string | null;
    тариф: string;
    основание: string;
    /** «14 900 ₽ ÷ 30 суток × 11 неиспользованных» — видит и суд. */
    расчёт: string;
    сумма: string;
    отклонение: boolean;
    причинаОтклонения: string | null;
    ктоОформил: string;
    оформлен: string;
    срок: string;
    просрочен: boolean;
    состояние: string;
  }>;
  основания: Array<{ код: string; ярлык: string; пункт: string }>;
  просрочено: number;
  mayWrite: boolean;
}

export interface PayAuto {
  строки: Array<{
    workspaceId: string;
    пространство: string;
    почта: string;
    тариф: string;
    сумма: string;
    следующее: string;
    уведомить: string;
    токен: string;
    попыток: string;
    исчерпаны: boolean;
    тревожная: boolean;
    снят: boolean;
  }>;
  попыток: number;
  тревожных: number;
  mayWrite: boolean;
}

export interface PayContracts {
  расторжения: Array<{
    id: string;
    пространство: string;
    основание: string;
    пункт: string;
    деньги: string;
    возвратПоложен: boolean | null;
    возвратId: string | null;
    когда: string;
  }>;
  основания: Array<{
    код: string;
    ярлык: string;
    пункт: string;
    кто: string;
    возврат: boolean | null;
    почему: string;
    деньги: string;
  }>;
  перечень: string[];
  приостановки: Array<{
    id: string;
    пространство: string;
    workspaceId: string;
    причина: string;
    состояние: string;
    простой: string;
    увидитКлиент: string;
    открыта: boolean;
  }>;
  причины: string[];
  акты: Array<{
    id: string;
    пространство: string;
    период: string;
    сумма: string;
    состояние: string;
    ярлык: string;
    номер: string | null;
    можно: boolean;
    почемуНельзя: string | null;
  }>;
  ждущихАктов: number;
  mayWrite: boolean;
}

// ── Что внутри пространства (пачка В) ──────────────────────────────

export interface SpaceInside {
  кабинеты: Array<{
    id: string;
    имя: string;
    продавец: string;
    ключЖив: boolean;
    отключён: boolean;
  }>;
  люди: Array<{
    userId: string;
    почта: string;
    роль: string;
    /** Владельца из пространства не убирают: оно осталось бы без хозяина. */
    владелец: boolean;
    вошёл: string | null;
  }>;
  mayWrite: boolean;
}

export const adminApi = {
  me: () => api<AdminMe>('/admin/me'),

  health: () => api<HealthSnapshot>('/admin/health'),

  resetBreaker: (cabinetId: string, domain: string) =>
    api<{ ok: true }>('/admin/health/breakers/reset', {
      method: 'POST',
      body: { cabinetId, domain },
    }),

  spaces: (q: string) =>
    api<{ spaces: AdminSpace[] }>(`/admin/spaces?q=${encodeURIComponent(q)}`),

  subscription: (id: string) => api<SpaceSubscription>(`/admin/spaces/${id}/subscription`),

  ledger: (id: string) =>
    api<{ computed: string; stored: string | null; drifted: boolean; entries: LedgerEntry[] }>(
      `/admin/spaces/${id}/ledger`,
    ),

  spacePayments: (id: string) =>
    api<{ payments: AdminPayment[] }>(`/admin/spaces/${id}/payments`),

  payments: () =>
    api<{
      payments: AdminPayment[];
      methods: PaymentMethodOption[];
      totalKopecks: number;
      totalText: string;
    }>('/admin/payments'),

  confirmPayment: (id: string, body: Record<string, unknown>) =>
    api<{
      payment: AdminPayment;
      expiresAt: string | null;
      started: boolean;
      planChanged: boolean;
    }>(`/admin/spaces/${id}/payments`, { method: 'POST', body }),

  extend: (id: string, days: number, reason: string) =>
    api<{ expiresAt: string }>(`/admin/spaces/${id}/extend`, { method: 'POST', body: { days, reason } }),

  /**
   * Заморозить. `days === null` — бессрочно.
   *
   * СРОК ПЕРЕДАЁТСЯ ЯВНО, а не «ноль значит навсегда»: ноль суток —
   * это оговорка, которую однажды пришлют всерьёз.
   */
  freeze: (id: string, reason: string, days: number | null) =>
    api<{ ok: true; until: string | null }>(`/admin/spaces/${id}/freeze`, {
      method: 'POST',
      body: { reason, days },
    }),

  unfreeze: (id: string) => api<{ ok: true }>(`/admin/spaces/${id}/unfreeze`, { method: 'POST', body: {} }),

  /**
   * Удалить человека навсегда.
   *
   * `confirmation` — НАБРАННАЯ ПОЧТА УДАЛЯЕМОГО. Не «да»: единственная
   * настоящая опасность такой кнопки — промах по соседней строке
   * списка, и от промаха «да/нет» не защищает совсем.
   */
  eraseUser: (userId: string, confirmation: string, reason: string) =>
    api<{ cabinets: number; spaces: number; email: string }>(`/admin/users/${userId}/erase`, {
      method: 'POST',
      body: { confirmation, reason },
    }),

  /**
   * Назначить тариф пространству, у которого его нет.
   *
   * ТОЛЬКО ЗАВЕДЕНИЕ. Смена действующего тарифа — отдельная ручка
   * (`changePlan`) и отдельная запись: назначение заводит подписку
   * там, где её не было, смена переписывает действующую.
   */
  setPlan: (
    id: string,
    planCode: string,
    reason: string,
    /**
     * Выбранные ступени (ТЗ 5). Не переданы — база: так тариф
     * выдавали до ступеней, и так же обязано выдаваться теперь.
     */
    ступени?: { cabStep: number; campStep: number },
  ) =>
    api<{ ok: true }>(`/admin/spaces/${id}/plan`, {
      method: 'POST',
      body: { planCode, reason, ...(ступени ?? {}) },
    }),

  /**
   * Сменить действующий тариф.
   *
   * `force` — согласие на переход «вниз». Без него сервер откажет
   * с текстом предупреждения: на тесном тарифе встанет пространство
   * ЦЕЛИКОМ, а не лишний кабинет.
   */
  changePlan: (
    id: string,
    /**
     * `cabStep`/`campStep` — ступени НОВОГО тарифа. Не переданы —
     * база: номер это место в лестнице этого тарифа, а не купленная
     * величина, и переносить его на другую лестницу нельзя.
     */
    body: {
      planCode: string;
      reason: string;
      force?: boolean;
      cabStep?: number;
      campStep?: number;
    },
  ) =>
    api<{ warning: string | null }>(`/admin/spaces/${id}/plan/change`, {
      method: 'POST',
      body,
    }),

  /** Что будет, если сменить, — до нажатия. */
  previewPlan: (id: string, planCode: string) =>
    api<PlanChangePreview>(
      `/admin/spaces/${id}/plan/preview?planCode=${encodeURIComponent(planCode)}`,
    ),

  /** Чем этот тариф был раньше. */
  planChanges: (id: string) =>
    api<{ changes: PlanChangeRow[] }>(`/admin/spaces/${id}/plan/changes`),

  /** Кем считать пространство: селлер или агентство. */
  setClientType: (id: string, clientType: string) =>
    api<{ ok: true }>(`/admin/spaces/${id}/client-type`, {
      method: 'POST',
      body: { clientType },
    }),

  /** Обнулить тариф: подписка кончается сегодня, записью в журнал. */
  resetPlan: (id: string, reason: string) =>
    api<{ expiresAt: string | null; changed: boolean }>(`/admin/spaces/${id}/plan/reset`, {
      method: 'POST',
      body: { reason },
    }),

  /**
   * Войти в чужое пространство под наблюдением (FR-22).
   *
   * ПРИЧИНА — ДОВОД, А НЕ ПОЛЕ ФОРМЫ. Она уезжает в журнал клиента,
   * и клиент её увидит: «разбираю обращение 4172» он поймёт,
   * а «проверка» прочитает как «к нам кто-то залез».
   */
  impersonate: (id: string, reason: string) =>
    api<{ id: string; workspaceId: string; startedAt: string; expiresAt: string; readOnly: true }>(
      `/admin/spaces/${id}/impersonate`,
      { method: 'POST', body: { reason } },
    ),

  /** Кого пора обезличить (НФ-14). */
  deletions: () =>
    api<{
      deletions: Array<{
        userId: string;
        email: string;
        requestedAt: string;
        readyAt: string;
        ready: boolean;
        reason: string | null;
      }>;
    }>('/admin/deletions'),

  executeDeletion: (userId: string) =>
    api<{ ok: true; cabinets: number; spaces: number }>(`/admin/deletions/${userId}/execute`, {
      method: 'POST',
      body: {},
    }),

  /**
   * Пробное письмо.
   *
   * «Настроена» и «работает» — разные вещи, и второе доказывается
   * только письмом, которое дошло.
   */
  testMail: (to: string) =>
    api<{ ok: true; to: string; at: string }>('/admin/mail/test', { method: 'POST', body: { to } }),

  endImpersonation: (id: string) =>
    api<{ ok: true }>(`/admin/impersonations/${id}/end`, { method: 'POST', body: {} }),

  block: (id: string, blocked: boolean, reason: string) =>
    api<{ ok: true }>(`/admin/spaces/${id}/block`, { method: 'POST', body: { blocked, reason } }),

  issueGrant: (id: string, body: Record<string, unknown>) =>
    api<{ id: string }>(`/admin/spaces/${id}/grants`, { method: 'POST', body }),

  /**
   * Сверка обхода пробного: «Проверить и протестировать» (п. 1 передачи).
   *
   * Не блокирует и ничего не меняет — отдаёт разбор и пишет его
   * в журнал клиента. Решение принимает человек.
   */
  bypassCheck: (id: string) =>
    api<
      | { checked: false; why: string }
      | {
          checked: true;
          report: {
            счёт: number;
            вывод: 'наверняка' | 'похоже' | 'слабо';
            признаки: Array<{
              ключ: string;
              название: string;
              исход: 'совпало' | 'разошлось' | 'нечем';
              подробность: string;
              вес: number;
            }>;
            строка: string;
          };
        }
    >(`/admin/spaces/${id}/bypass-check`, { method: 'POST', body: {} }),

  /** Что можно назначить этому пространству — и что у него сейчас. */
  assignable: (id: string) =>
    api<{
      /** `features` — разделы ДЕЙСТВУЮЩИЕ, с учётом уже выданных условий. */
      current: {
        code: string;
        name: string;
        limits: Record<string, number>;
        /** Потолки словами эталона: «1 кабинет, до 50 кампаний». */
        limitsWords: string;
        features: string[];
      };
      limitTitles: Record<string, string>;
      /** Справочник разделов — тем же ответом, а не вторым запросом. */
      modules: ServiceModule[];
      plans: Array<{
        code: string;
        name: string;
        tagline: string | null;
        limits: Record<string, number>;
        /** Потолки словами эталона — те же, что на карточке сайта. */
        limitsWords: string;
        /** Лестницы: по ним рисуются сегменты выбора ступени (ТЗ 5). */
        cabSteps: AdminStep[];
        campSteps: AdminStep[];
        shelf: string;
        shelfTitle: string;
        terms: Array<{ id: string; name: string; days: number | null; priceKopecks: number | null }>;
      }>;
    }>(`/admin/spaces/${id}/assignable`),

  /** Счета, ждущие оплаты (передача, п. 9). */
  invoices: () =>
    api<{
      invoices: Array<{
        id: string;
        number: number;
        workspaceId: string;
        planName: string;
        amountText: string;
        note: string;
        issuedAt: string;
        dueAt: string | null;
      }>;
    }>('/admin/invoices'),

  issueInvoice: (spaceId: string, body: Record<string, unknown>) =>
    api<{ id: string; number: number }>(`/admin/spaces/${spaceId}/invoices`, { method: 'POST', body }),

  /** Отметить оплаченным: проводит оплату и закрывает счёт разом. */
  invoicePaid: (id: string, body: Record<string, unknown>) =>
    api<{ id: string; state: string }>(`/admin/invoices/${id}/paid`, { method: 'POST', body }),

  invoiceCancel: (id: string) =>
    api<{ id: string; state: string }>(`/admin/invoices/${id}/cancel`, { method: 'POST', body: {} }),

  /** Тариф с историей оплат не удаляется: запрет на сервере. */
  deletePlan: (id: string) => api<{ ok: true }>(`/admin/plans/${id}`, { method: 'DELETE' }),

  planUsage: (id: string) =>
    api<{ subscriptions: number; payments: number; refusal: string | null }>(
      `/admin/plans/${id}/usage`,
    ),

  plans: () =>
    api<{
      plans: AdminPlan[];
      modules: ServiceModule[];
      audiences: PlanAudience[];
      /** Пять разделов тарифа — списком с сервера (передача, п. 3). */
      shelves: Array<{ code: string; title: string }>;
      grid: GridState;
    }>('/admin/plans'),

  publishGrid: (published: boolean, reason: string) =>
    api<GridState>('/admin/plans/publish', { method: 'POST', body: { published, reason } }),

  prices: (termId: string) => api<{ prices: PlanPrice[] }>(`/admin/terms/${termId}/prices`),

  setTrialPlan: (planId: string, trial: boolean) =>
    api<{ trialPlanId: string | null }>(`/admin/plans/${planId}/trial`, {
      method: 'POST',
      body: { trial },
    }),

  setFreePlan: (planId: string, free: boolean) =>
    api<{ freePlanId: string | null }>(`/admin/plans/${planId}/free`, {
      method: 'POST',
      body: { free },
    }),

  addTerm: (planId: string, body: Record<string, unknown>) =>
    api<{ id: string }>(`/admin/plans/${planId}/terms`, { method: 'POST', body }),

  archiveTerm: (termId: string) =>
    api<{ ok: true }>(`/admin/terms/${termId}/archive`, { method: 'POST', body: {} }),

  bulkList: () =>
    api<{ scopes: Array<{ code: string; title: string }>; bulk: BulkRow[] }>('/admin/bulk'),

  bulkPreview: (body: Record<string, unknown>) =>
    api<{ take: number; skip: number; words: string }>('/admin/bulk/preview', { method: 'POST', body }),

  bulkCreate: (body: Record<string, unknown>) =>
    api<{ id: string }>('/admin/bulk', { method: 'POST', body }),

  /** Пространства по почтам владельцев. Ненайденные возвращаются. */
  bulkByEmails: (emails: string[]) =>
    api<{ found: Array<{ workspaceId: string; email: string }>; missing: string[] }>(
      '/admin/bulk/emails',
      { method: 'POST', body: { emails } },
    ),

  bulkApply: (id: string) =>
    api<{ affected: number; skipped: number }>(`/admin/bulk/${id}/apply`, { method: 'POST', body: {} }),

  createPlan: (body: Record<string, unknown>) =>
    api<{ id: string; code: string }>('/admin/plans', { method: 'POST', body }),

  updatePlan: (id: string, body: Record<string, unknown>) =>
    api<{ id: string }>(`/admin/plans/${id}`, { method: 'PATCH', body }),

  addPrice: (termId: string, body: Record<string, unknown>) =>
    api<{ id: string }>(`/admin/terms/${termId}/prices`, { method: 'POST', body }),

  /**
   * Журнал пространства — для разбора обращений.
   *
   * ЭТО ЧТЕНИЕ, И ТОЛЬКО ЧТЕНИЕ. Вход под наблюдением (FR-22) остаётся
   * отдельной, более тяжёлой дверью; ради вопроса «что тут случилось»
   * открывать её незачем.
   */
  spaceJournal: (id: string, onlyErrors: boolean) =>
    api<{ journal: AdminJournalRow[] }>(
      `/admin/spaces/${id}/journal${onlyErrors ? '?errors=1' : ''}`,
    ),

  /** Пишет ли сервис в Wildberries прямо сейчас (аварийный рубильник, ТЗ 13.2). */
  writes: () => api<WritesState>('/admin/writes'),

  setWrites: (on: boolean, reason: string) =>
    api<WritesState>('/admin/writes', { method: 'POST', body: { on, reason } }),

  /**
   * ═══════════════════════════════════════════════════════════════════
   * СТИРАНИЕ КЛЮЧЕЙ — ДВА ВЫЗОВА, И ЭТО НАРОЧНО.
   *
   * Сначала «сколько сотрётся», потом «стереть». Красное окно без
   * чисел спрашивает «вы уверены» ни о чём: «сотрём 14 ключей у 9
   * кабинетов» и «сотрём ноль» — два разных решения, и второе
   * принимать не надо вовсе.
   * ═══════════════════════════════════════════════════════════════════
   */
  eraseKeysPreview: () => api<EraseKeysPreview>('/admin/keys/erase'),

  eraseKeys: (confirm: string, reason: string) =>
    api<{ ключей: number; названий: number }>('/admin/keys/erase', {
      method: 'POST',
      body: { confirm, reason },
    }),

  grants: () => api<{ grants: AdminGrant[] }>('/admin/grants'),

  revokeGrant: (id: string, reason: string) =>
    api<{ ok: true }>(`/admin/grants/${id}/revoke`, { method: 'POST', body: { reason } }),

  staff: () =>
    api<{
      roles: StaffRole[];
      sections: RightSection[];
      undeniable: string[];
      /** Заведённые роли и лестницы — тем же ответом, одним запросом. */
      builtRoles: BuiltRole[];
      groups: RightGroup[];
      twofa: string;
      staff: StaffMember[];
    }>('/admin/staff'),

  /** Выдать доступ на ЗАВЕДЁННУЮ роль. Имя необязательно. */
  grantAccess: (body: { email: string; roleId: string; reason: string; name?: string }) =>
    api<{ id: string; roleId: string | null; role: string | null; email: string }>(
      '/admin/staff/access',
      { method: 'POST', body },
    ),

  /**
   * Переименовать сотрудника.
   *
   * ПУСТАЯ СТРОКА РАЗРЕШЕНА и означает «имя не названо»: строка списка
   * вернётся к почте. Запрети очистку — и опечатка, набранная один раз,
   * осталась бы навсегда.
   */
  renameStaff: (userId: string, name: string) =>
    api<{ name: string }>(`/admin/staff/${userId}/name`, { method: 'PATCH', body: { name } }),

  /** Перевести сотрудника на другую роль. */
  moveStaffRole: (userId: string, roleId: string) =>
    api<{ roleId: string | null; role: string | null }>(`/admin/staff/${userId}/role`, {
      method: 'POST',
      body: { roleId },
    }),

  roles: () => api<{ roles: BuiltRole[] }>('/admin/roles'),

  /** Журнал ошибок сайта: одна строка на (адрес, код). */
  siteErrors: () =>
    api<{
      level: string;
      text: string;
      lines: Array<{
        path: string;
        code: number;
        count: number;
        firstAt: string;
        lastAt: string;
        note: string | null;
        level: string;
        text: string;
      }>;
    }>('/admin/site-errors'),

  forgetSiteError: (body: { path?: string; code?: number; all?: boolean }) =>
    api<{ forgotten: number }>('/admin/site-errors/forget', { method: 'POST', body }),

  createRole: (body: { name: string; note?: string; grid?: RoleGrid }) =>
    api<{ id: string; name: string }>('/admin/roles', { method: 'POST', body }),

  updateRole: (id: string, body: { name?: string; note?: string; grid?: RoleGrid }) =>
    api<{ id: string; name: string; grid: RoleGrid }>(`/admin/roles/${id}`, {
      method: 'PATCH',
      body,
    }),

  deleteRole: (id: string) => api<{ ok: true }>(`/admin/roles/${id}`, { method: 'DELETE' }),

  /** Выдать доступ по ПОЧТЕ: «идентификатор» никто не знает наизусть. */
  grantRole: (body: { email: string; role: string; reason: string }) =>
    api<{ id: string; email: string }>('/admin/staff', { method: 'POST', body }),

  /**
   * Снять или вернуть права поимённо.
   *
   * ПРИХОДИТ ПОЛНЫЙ НАБОР СНЯТОГО, а не «снять это одно»: экран правят
   * несколькими переключателями сразу, и половина уехавших изменений
   * хуже, чем ни одного.
   */
  setStaffRights: (userId: string, denied: string[]) =>
    api<{ denied: string[]; changedAt: string | null }>(`/admin/staff/${userId}/rights`, {
      method: 'POST',
      body: { denied },
    }),

  revokeRole: (userId: string, reason: string) =>
    api<{ ok: true }>(`/admin/staff/${userId}/revoke`, { method: 'POST', body: { reason } }),

  // ── Признаки выпуска (ТЗ 19, часть II) ────────────────────────────

  flags: () => api<ReleaseFlags>('/admin/flags'),

  createFlag: (body: { key: string; note?: string }) =>
    api<ReleaseFlag>('/admin/flags', { method: 'POST', body }),

  /**
   * Переключить.
   *
   * `confirm` уходит ТОЛЬКО при `on`, и это не мелочь: поле, которое
   * шлют всегда, читается сервером как «подтверждение прислали»,
   * и однажды окажется, что подтверждает оно пустоту.
   */
  switchFlag: (
    key: string,
    body: { state: 'off' | 'workspaces' | 'on'; workspaces?: string[]; confirm?: string },
  ) => api<ReleaseFlag>(`/admin/flags/${encodeURIComponent(key)}`, { method: 'PATCH', body }),

  /** Снять: строка остаётся, на ней держится запрет ключа. */
  retireFlag: (key: string) =>
    api<ReleaseFlag>(`/admin/flags/${encodeURIComponent(key)}`, { method: 'DELETE' }),

  // ── Персональные данные (ТЗ часть I раздел 3) ─────────────────────

  /**
   * Числа на пяти закладках — одним запросом.
   *
   * Пять запросов дали бы пять появляющихся вразнобой чисел; на глаз
   * это читается как подмигивание, и первое, что делает человек, —
   * жмёт обновление.
   */
  pdTabs: () => api<PdTabs>('/admin/pd/tabs'),

  pdRequests: (filter: string, kind?: string) =>
    api<PdRequests>(
      `/admin/pd/requests?filter=${encodeURIComponent(filter)}` +
        (kind === undefined ? '' : `&kind=${encodeURIComponent(kind)}`),
    ),

  pdRequest: (id: string) => api<PdRequestCard>(`/admin/pd/requests/${encodeURIComponent(id)}`),

  /**
   * «Открыть» — ЗАПИСЬ, а не просмотр: обращение переходит в «в работе»
   * и в журнале остаётся след «кто взял». Два человека, независимо
   * взявшихся за одно обращение, — это два разных ответа заявителю.
   */
  pdOpenRequest: (id: string) =>
    api<{ ok: true }>(`/admin/pd/requests/${encodeURIComponent(id)}/open`, { method: 'POST' }),

  pdExtendRequest: (id: string, reason: string) =>
    api<PdRequestRow>(`/admin/pd/requests/${encodeURIComponent(id)}/extend`, {
      method: 'POST',
      body: { reason },
    }),

  /** Ответить и закрыть — одним действием: отдельного «закрыть» нет. */
  pdAnswerRequest: (id: string, answer: string, files: string[]) =>
    api<PdRequestRow>(`/admin/pd/requests/${encodeURIComponent(id)}/answer`, {
      method: 'POST',
      body: { answer, files },
    }),

  pdRkn: () => api<PdRkn>('/admin/pd/rkn'),

  pdRknFiling: (body: { when: string; what: string }) =>
    api<{ ok: true }>('/admin/pd/rkn/filing', { method: 'POST', body }),

  pdEditions: () => api<PdEditions>('/admin/pd/editions'),

  pdEditionText: (id: string) =>
    api<{ имя: string; текст: string; откуда: string }>(
      `/admin/pd/editions/${encodeURIComponent(id)}/text`,
    ),

  pdEditionAccepts: (id: string) =>
    api<{ accepts: Array<{ userId: string; email: string | null; когда: string }> }>(
      `/admin/pd/editions/${encodeURIComponent(id)}/accepts`,
    ),

  /**
   * Опубликовать редакцию. НОМЕР НЕ ПЕРЕДАЁТСЯ ВОВСЕ — его считает
   * сервер: две редакции с одним номером означают неразбираемое
   * «с чем именно человек согласился».
   */
  pdCreateEdition: (body: {
    document: string;
    effectiveFrom: string;
    source: 'file' | 'text';
    file?: string;
    text?: string;
    changes: string[];
  }) => api<PdEditionRow>('/admin/pd/editions', { method: 'POST', body }),

  pdProcessors: () => api<PdProcessors>('/admin/pd/processors'),

  pdAddProcessor: (body: { name: string; inn: string; what: string; whereAt: string }) =>
    api<PdProcessorRow>('/admin/pd/processors', { method: 'POST', body }),

  pdProcessorMandate: (
    id: string,
    body: { file: string; number: string; date: string; signedBy: string },
  ) =>
    api<PdProcessorRow>(`/admin/pd/processors/${encodeURIComponent(id)}/mandate`, {
      method: 'POST',
      body,
    }),

  /** Исключить — это ДАТА, а не удаление: по ней видно, кто и когда. */
  pdProcessorOff: (id: string, body: { when: string; why: string }) =>
    api<PdProcessorRow>(`/admin/pd/processors/${encodeURIComponent(id)}/off`, {
      method: 'POST',
      body,
    }),

  pdAdmissions: () => api<PdAdmissions>('/admin/pd/admissions'),

  pdGrantAdmission: (body: {
    fio: string;
    post?: string;
    categories: string[];
    since: string;
    staffUserId?: string;
  }) => api<PdAdmissionRow>('/admin/pd/admissions', { method: 'POST', body }),

  /** Номер редакции берёт сервер из действующей — не с экрана. */
  pdAdmissionKnown: (id: string, body: { when?: string }) =>
    api<PdAdmissionRow>(`/admin/pd/admissions/${encodeURIComponent(id)}/known`, {
      method: 'POST',
      body,
    }),

  pdRevokeAdmission: (id: string) =>
    api<PdAdmissionRow>(`/admin/pd/admissions/${encodeURIComponent(id)}/revoke`, {
      method: 'POST',
    }),

  // ── Наблюдение сверх здоровья службы (ТЗ часть II раздел 3) ───────

  nabTabs: () => api<NabTabs>('/admin/nab/tabs'),

  nabKeep: () => api<NabKeep>('/admin/nab/keep'),

  /**
   * «Убрать сейчас» СТАВИТ В ОЧЕРЕДЬ, а не удаляет из обработчика
   * запроса: настоящую чистку делает уборщик по расписанию — у него
   * пределы на пачку, откат при обрыве и журнал.
   */
  nabKeepRun: () =>
    api<{ убрано: number; очередь: number }>('/admin/nab/keep/run', { method: 'POST' }),

  nabDeletions: () => api<NabDeletions>('/admin/nab/deletions'),

  /** Обезличить: слово подтверждения проверяет сервер, а не окно. */
  nabAnonymize: (id: string, confirm: string) =>
    api<{ id: string }>(`/admin/nab/deletions/${encodeURIComponent(id)}/anonymize`, {
      method: 'POST',
      body: { confirm },
    }),

  nabCancelDeletion: (id: string, why: string) =>
    api<{ id: string }>(`/admin/nab/deletions/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
      body: { why },
    }),

  nabLetters: (filter: string) =>
    api<NabLetters>(`/admin/nab/letters?filter=${encodeURIComponent(filter)}`),

  nabLetter: (id: string) => api<NabLetter>(`/admin/nab/letters/${encodeURIComponent(id)}`),

  nabAnswerLetter: (
    id: string,
    body: { text: string; files?: string[]; when?: string; time?: string },
  ) =>
    api<{ id: string }>(`/admin/nab/letters/${encodeURIComponent(id)}/answer`, {
      method: 'POST',
      body,
    }),

  nabClaims: () => api<NabClaims>('/admin/nab/claims'),

  nabTakeClaim: (id: string) =>
    api<{ id: string }>(`/admin/nab/claims/${encodeURIComponent(id)}/take`, { method: 'POST' }),

  /**
   * Ответ на претензию. `refundKopecks` НЕ ПЕРЕДАЁТСЯ, когда денег
   * не возвращаем: претензию удовлетворяют и не деньгами, и пустое
   * поле здесь — обычный ответ, а не недоделка.
   */
  nabAnswerClaim: (
    id: string,
    body: { verdict: string; text: string; refundKopecks?: number },
  ) =>
    api<{ id: string }>(`/admin/nab/claims/${encodeURIComponent(id)}/answer`, {
      method: 'POST',
      body,
    }),

  // ── Подразделы «Оплат» (ТЗ часть II раздел 2) ─────────────────────

  payTabs: () => api<PayTabs>('/admin/pay/tabs'),

  payRefunds: () => api<PayRefunds>('/admin/pay/refunds'),

  /**
   * Оформить возврат. РАСЧЁТ УХОДИТ ТРЕМЯ ЧИСЛАМИ, а не итогом:
   * «14 900 ₽ ÷ 30 суток × 11 неиспользованных» видит и клиент,
   * и суд, а итог без них через год превращается в число ниоткуда.
   * Сложить их сервер умеет сам, и ровно одним способом.
   */
  payCreateRefund: (body: {
    workspaceId?: string;
    workspaceName: string;
    planText?: string;
    reason: string;
    paidKopecks: number;
    periodDays: number;
    unusedDays: number;
    manualKopecks?: number;
    manualWhy?: string;
  }) => api<{ id: string }>('/admin/pay/refunds', { method: 'POST', body }),

  payRefundPaid: (id: string) =>
    api<{ id: string }>(`/admin/pay/refunds/${encodeURIComponent(id)}/paid`, {
      method: 'POST',
    }),

  payAuto: () => api<PayAuto>('/admin/pay/auto'),

  payAutoSum: (workspaceId: string, sumKopecks: number) =>
    api<{ workspaceId: string }>(`/admin/pay/auto/${encodeURIComponent(workspaceId)}`, {
      method: 'PATCH',
      body: { sumKopecks },
    }),

  payAutoOff: (workspaceId: string, why: string) =>
    api<{ workspaceId: string }>(`/admin/pay/auto/${encodeURIComponent(workspaceId)}`, {
      method: 'DELETE',
      body: { why },
    }),

  payContracts: () => api<PayContracts>('/admin/pay/contracts'),

  payTerminate: (body: {
    workspaceId?: string;
    workspaceName: string;
    reason: string;
    when: string;
    note?: string;
  }) => api<{ id: string }>('/admin/pay/contracts/terminate', { method: 'POST', body }),

  payPause: (workspaceId: string, why: string) =>
    api<{ id: string }>('/admin/pay/contracts/pause', {
      method: 'POST',
      body: { workspaceId, why },
    }),

  payResume: (id: string) =>
    api<{ id: string }>(`/admin/pay/contracts/pause/${encodeURIComponent(id)}/resume`, {
      method: 'POST',
    }),

  payAct: (id: string) =>
    api<{ id: string; номер: string | null }>(
      `/admin/pay/contracts/acts/${encodeURIComponent(id)}`,
      { method: 'POST' },
    ),

  // ── Что внутри пространства (пачка В) ─────────────────────────────

  spaceInside: (id: string) =>
    api<SpaceInside>(`/admin/spaces/${encodeURIComponent(id)}/inside`),

  /** Отключить кабинет: кабинет остаётся, доступ пространства — нет. */
  spaceCabinetOff: (id: string, cabinetId: string) =>
    api<{ ok: true }>(
      `/admin/spaces/${encodeURIComponent(id)}/cabinets/${encodeURIComponent(cabinetId)}/off`,
      { method: 'POST' },
    ),

  /** Убрать человека: учётная запись остаётся, участие — нет. */
  spaceMemberOff: (id: string, userId: string) =>
    api<{ ok: true }>(
      `/admin/spaces/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}/off`,
      { method: 'POST' },
    ),

  // ── Заморозка всех подписок (пачка Е) ─────────────────────────────

  freezeAll: () => api<ВидЗаморозки>('/admin/freeze-all'),

  /** Причина обязательна: её увидит клиент вместо остатка суток. */
  freezeAllOn: (reason: string) =>
    api<{ ok: true }>('/admin/freeze-all', { method: 'POST', body: { reason } }),

  /** `note` — «что починилось»: вторая запись в журнал и письмо всем. */
  freezeAllOff: (note: string) =>
    api<{ простой: number }>('/admin/freeze-all/off', { method: 'POST', body: { note } }),
};

/** Что показывает панель заморозки над формой раздачи. */
export interface ВидЗаморозки {
  идёт: boolean;
  область: string;
  причинаВиднаКлиенту: boolean;
  размораживаютРуками: boolean;
  письмоПриРазморозке: string;
  сейчас: {
    причина: string;
    пространств: number;
    простойСуток: number;
    вернём: string;
    увидитКлиент: string;
    с: string;
  } | null;
  отличия: Array<{ чем: string; раздача: string; заморозка: string }>;
  история: Array<{
    id: string;
    с: string;
    по: string | null;
    причина: string;
    причинаСнятия: string | null;
    пространств: number;
  }>;
}
